// Explainer landing page targeting "wallet with built-in tracker" and
// "trackable wallet" searches. FAQ answers come straight from the sitewide
// FAQ so the two never drift apart.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import GuideVideo from "../components/GuideVideo";
import { FAQ_GROUPS } from "../components/FAQSection";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
  makeVideoJsonLd,
} from "../seo/structuredData";
import trackerPhoto from "../assets/newest-version/Integrated Tracking Card.jpg";

const futura = "'Futura', sans-serif";

const TRACKING_FAQ =
  FAQ_GROUPS.find((g) => g.category === "Tracking Card")?.items || [];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const trackingVideoJsonLd = makeVideoJsonLd({
  name: "How the Brik Wallet Tracking Works",
  description:
    "A real demo of the Brik's built-in tracking card: ringing the wallet and finding it from a phone.",
  poster: "/videos/brik-wallet-tracking-demo-poster.jpg",
  src: "/videos/brik-wallet-tracking-demo.mp4",
  duration: "PT1M14S",
});

const TrackerWallet = () => {
  const faqJsonLd = makeFaqJsonLd(TRACKING_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Wallet with Built-In Tracker", path: "/wallet-with-built-in-tracker" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Wallet with a Built-In Tracker"
        description="The Brik is a metal wallet with a rechargeable tracking card built in. See its location from your phone, ring it, and recharge twice a year. $69.99."
        path="/wallet-with-built-in-tracker"
        keywords="wallet with built-in tracker, trackable wallet, tracking wallet, wallet tracker card, smart wallet, find my wallet, wallet you can track from your phone"
        jsonLd={[faqJsonLd, breadcrumbJsonLd, trackingVideoJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "780px", margin: "0 auto" }}>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(32px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            The Wallet with a Built-In Tracker
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.55,
              color: "#cfcfcf",
              maxWidth: "620px",
              margin: "0 auto 26px",
            }}
          >
            The Brik is a $69.99 metal wallet with a rechargeable tracking
            card integrated into the wallet itself. See its location on a map,
            ring it from your phone when it hides, and recharge it on any
            wireless charger about twice a year. No AirTag bulge, no extra
            purchase, and it works with Apple or Android.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get the Metal Brik · $69.99
          </a>
          <div style={{ maxWidth: "540px", margin: "36px auto 0" }}>
            <img
              fetchPriority="high"
              src={trackerPhoto}
              alt="The rechargeable tracking card sliding into the Metal Brik wallet"
              style={{ width: "100%", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            How the tracking card works
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "24px",
            }}
          >
            {[
              {
                step: "1",
                title: "It lives inside the wallet",
                text: "The tracker is shaped like a card and slides into its own slot, so the wallet stays slim. It's securely integrated but removable whenever you want.",
              },
              {
                step: "2",
                title: "Your phone knows where it is",
                text: "Open the tracking app to see your wallet's location on a map, or make the card ring when it's hiding nearby. Setup instructions and a video guide come with every order.",
              },
              {
                step: "3",
                title: "Recharge twice a year",
                text: "The battery lasts up to 6 months per charge. When it runs low, set the card on any standard wireless charger. No battery swaps, no subscriptions.",
              },
            ].map((s) => (
              <div
                key={s.step}
                style={{
                  background: "#f7f7f7",
                  borderRadius: "14px",
                  padding: "26px 24px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <div
                  style={{
                    fontFamily: futura,
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    background: "#ff7d1a",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "17px",
                    marginBottom: "14px",
                  }}
                >
                  {s.step}
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "18px",
                    margin: "0 0 8px",
                    color: "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "15px", margin: 0 }}>{s.text}</p>
              </div>
            ))}
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "26px 0 0",
            }}
          >
            Want to see the setup?{" "}
            <a href="/setup" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Watch the setup video
            </a>
            .
          </p>
        </div>
      </section>

      {/* Video demo */}
      <GuideVideo
        heading="Watch the tracking work"
        caption="Someone asked us to show how the tracking actually works. This is the answer, unedited."
        src="/videos/brik-wallet-tracking-demo.mp4"
        poster="/videos/brik-wallet-tracking-demo-poster.jpg"
      />

      {/* vs AirTag wallets */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>Tracking card vs AirTag wallets</h2>
          <p style={bodyText}>
            Most "trackable" wallets are ordinary wallets with a pocket for an
            AirTag you buy separately. An AirTag is coin-shaped, so those
            wallets carry a visible bump and only work in the Apple ecosystem.
          </p>
          <p style={bodyText}>
            The Brik takes a different approach: the tracker is a card, the
            wallet was designed around it, and it comes included. Pick Apple or
            Android at checkout so your Brik ships with the right tracking card
            for your phone. And since the card is removable, the Brik still
            works as a normal metal wallet if you ever want to carry it
            without tracking.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            See the wallet
          </a>
          <p style={{ ...bodyText, fontSize: "14px", margin: "18px 0 0" }}>
            Comparing metal wallets? Read{" "}
            <a href="/brik-vs-ridge" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Brik vs Ridge
            </a>
            . Heading to campus? See the{" "}
            <a
              href="/wallet-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              wallet for college students
            </a>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Tracking card questions, answered
          </h2>
          {TRACKING_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrackerWallet;
